if (!customElements.get('rr-drop-tiles')) {
  customElements.define(
    'rr-drop-tiles',
    class RrDropTiles extends HTMLElement {
      connectedCallback() {
        this.tiles = [...this.querySelectorAll('[data-rr-tile]')];
        this.panels = [...this.querySelectorAll('[data-rr-panel]')];
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this.onClick = this.onClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('keyup', this.onKeyUp);

        // Door offsets are in px, so they must follow the container width.
        this.resizeObserver = new ResizeObserver(() => this.layout());
        this.resizeObserver.observe(this);
        this.onBreakpoint = () => this.layout();
        this.mobileQuery.addEventListener('change', this.onBreakpoint);

        const hashTile = window.location.hash && this.tiles.find((tile) => `#${tile.id}` === window.location.hash);
        if (hashTile) this.open(hashTile);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keyup', this.onKeyUp);
        this.resizeObserver.disconnect();
        this.mobileQuery.removeEventListener('change', this.onBreakpoint);
        clearTimeout(this.scrollTimer);
      }

      onClick(event) {
        const opener = event.target.closest('[data-rr-open]');
        if (opener) {
          this.open(opener.closest('[data-rr-tile]'));
          return;
        }
        if (event.target.closest('[data-rr-close]')) this.close();
      }

      onKeyUp(event) {
        if (event.key === 'Escape' && this.activeTile) this.close();
      }

      open(tile) {
        if (!tile || tile === this.activeTile || !this.panels.length) return;
        this.activeTile = tile;
        this.classList.add('is-open');
        this.layout();

        const activeIndex = this.tiles.indexOf(tile);
        this.tiles.forEach((other) => {
          other.querySelector('.rr-tile__card')?.setAttribute('aria-expanded', String(other === tile));
        });
        this.panels.forEach((panel, index) => {
          const active = index === activeIndex;
          panel.classList.toggle('is-active', active);
          panel.inert = !active;
          if (active) panel.querySelectorAll('.rr-panel__body, .rr-panel__info').forEach((el) => el.scrollTo({ top: 0 }));
        });

        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.scrollIntoViewIfNeeded(), 80);
        this.panels[activeIndex]?.querySelector('.rr-panel__close')?.focus({ preventScroll: true });
      }

      close() {
        const previous = this.activeTile;
        this.activeTile = null;
        this.classList.remove('is-open');
        this.layout();
        this.tiles.forEach((tile) => tile.querySelector('.rr-tile__card')?.setAttribute('aria-expanded', 'false'));
        this.panels.forEach((panel) => {
          panel.classList.remove('is-active');
          panel.inert = true;
        });
        previous?.querySelector('.rr-tile__card')?.focus({ preventScroll: true });
      }

      // Cards keep their size (so the block never changes height): cards before the active one slide
      // out to the start, cards after it to the end, each leaving a strip; the panel fills the gap.
      // Desktop slides along the row (left/right), mobile along the stack (up/down).
      layout() {
        const activeIndex = this.activeTile ? this.tiles.indexOf(this.activeTile) : -1;
        const styles = getComputedStyle(this);
        const strip = parseFloat(styles.getPropertyValue('--rr-strip')) || 25;
        const mobile = this.mobileQuery.matches;
        const gap = parseFloat(mobile ? styles.rowGap : styles.columnGap) || 0;
        const size = mobile ? this.clientHeight : this.clientWidth;
        const before = Math.max(activeIndex, 0);
        const after = activeIndex < 0 ? 0 : this.tiles.length - 1 - activeIndex;

        this.tiles.forEach((tile, index) => {
          let shift = 0;
          const side = activeIndex < 0 || index === activeIndex ? '' : index < activeIndex ? 'left' : 'right';
          const start = mobile ? tile.offsetTop : tile.offsetLeft;
          const length = mobile ? tile.offsetHeight : tile.offsetWidth;
          if (side === 'left') {
            const stripEnd = index * (strip + gap) + strip;
            shift = stripEnd - (start + length);
          } else if (side === 'right') {
            const fromEnd = this.tiles.length - index;
            const stripStart = size - fromEnd * strip - (fromEnd - 1) * gap;
            shift = stripStart - start;
          }
          tile.style.setProperty('--rr-shift', `${shift}px`);
          tile.classList.toggle('is-active', index === activeIndex);
          tile.classList.toggle('is-left', side === 'left');
          tile.classList.toggle('is-right', side === 'right');
        });

        this.style.setProperty('--rr-panel-start', `${before * (strip + gap)}px`);
        this.style.setProperty('--rr-panel-end', `${after * (strip + gap)}px`);
      }

      scrollIntoViewIfNeeded() {
        const header = document.querySelector('.section-header');
        const headerHeight = header ? header.getBoundingClientRect().bottom : 0;
        const rect = this.getBoundingClientRect();
        if (rect.top >= headerHeight && rect.bottom <= window.innerHeight) return;
        window.scrollTo({ top: Math.max(0, rect.top + window.scrollY - headerHeight - 10), behavior: 'smooth' });
      }
    }
  );
}
